import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

export interface IIconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Accessible label; also used as the hover tooltip. */
  label: string;
  /** `danger` tints the icon with the error color (e.g. confirm-delete). */
  tone?: 'neutral' | 'danger';
  children: React.ReactNode;
}

/**
 * Borderless icon-only button for dense operator rows — no box, just the icon
 * tinted on hover/focus. Use where a boxed {@link Button} would be too heavy
 * (table row actions, inline comment controls). Keeps a visible focus ring per
 * the MOPS keyboard-first rule.
 */
const IconButton = ({ label, tone = 'neutral', className, type = 'button', children, ...props }: IIconButtonProps) => (
  <button
    type={type}
    aria-label={label}
    title={label}
    className={cx(
      'flex shrink-0 cursor-pointer items-center justify-center rounded-sm p-0.5 transition-colors',
      'focus-visible:outline-border-brand focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1',
      'disabled:cursor-not-allowed disabled:opacity-50',
      tone === 'danger' ? 'text-fg-error-primary hover:opacity-80' : 'text-fg-quaternary hover:text-fg-primary',
      className
    )}
    {...props}
  >
    {children}
  </button>
);

export default IconButton;
